import { useCallback, useEffect, useState } from "react"
import { create } from "zustand" // move to lib/states

import { cn } from "@productlane/lib/src/browser"
import { typographyVariants } from "@productlane/ui"

import type { EditorProps } from "@tiptap/pm/view"
import { generateId } from "@/lib/id"

export const CURSOR_COLORS = [
	"#ffcc00",
	"#22c55e",
	"#f59e0b",
	"#06b6d4",
	"#3b82f6",
	"#a855f7",
]
export function getRandomCursorColor() {
	return CURSOR_COLORS[Math.floor(Math.random() * CURSOR_COLORS.length)]!
}

export const DEFAULT_EDITOR_PROPS: EditorProps & {
	attributes: { class: string }
} = {
	attributes: {
		class: cn(
			"focus:outline-none",
			typographyVariants({ variant: "editor", mode: "invert" }),
		),
	},
}

interface Highlights {
	prevHoveredIds: Array<string>
	hoveredIds: Array<string>
	clickCount: number
	setHoveredIds: (hoveredIds: Array<string>) => void
	setPrevHoveredIds: (prevHoveredIds: Array<string>) => void
	setClickCount: (clickCount: number) => void
}

const useHighlightState = create<Highlights>()((set) => ({
	prevHoveredIds: [],
	hoveredIds: [],
	clickCount: 0,
	setHoveredIds: (hoveredIds) => {
		set({ hoveredIds })
	},
	setPrevHoveredIds: (prevHoveredIds) => {
		set({ prevHoveredIds })
	},
	setClickCount: (clickCount) => {
		set({ clickCount })
	},
}))

export function useHighlights() {
	const {
		hoveredIds,
		setHoveredIds,
		clickCount,
		setClickCount,
		prevHoveredIds,
		setPrevHoveredIds,
	} = useHighlightState()

	const updateHoveredIds = useCallback(
		(value: string[]) => {
			setPrevHoveredIds(hoveredIds)
			setHoveredIds(value)
			setClickCount(0)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[hoveredIds],
	)

	const [highlightId, setHighlightId] = useState("")

	useEffect(() => {
		const docMarks = document?.querySelectorAll("mark[data-id]")
		const marks = Array.from(docMarks) as Array<HTMLElement>

		function mouseLeaveFunction() {
			updateHoveredIds([])
		}

		// Keep track of the event handlers for cleanup
		const mouseEnterFunctions: Record<string, () => void> = {}

		for (const mark of marks) {
			const id = mark.attributes.getNamedItem("data-id")?.value
			if (id) {
				const mouseEnterFunction = () => {
					updateHoveredIds([id])
				}

				// Add event listeners
				mark.addEventListener("mouseenter", mouseEnterFunction)
				mark.addEventListener("mouseleave", mouseLeaveFunction)

				// Store the reference for cleanup
				mouseEnterFunctions[id] = mouseEnterFunction
			}
		}

		return () => {
			for (const mark of marks) {
				const id = mark.attributes.getNamedItem("data-id")?.value
				if (id && mouseEnterFunctions[id]) {
					// Remove event listeners
					mark.removeEventListener("mouseenter", mouseEnterFunctions[id])
					mark.removeEventListener("mouseleave", mouseLeaveFunction)
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [highlightId])

	const setHighlights = useCallback(() => {
		setHighlightId(generateId())
	}, [])

	return {
		clickCount,
		hoveredIds,
		prevHoveredIds,
		setClickCount,
		setHighlights,
		setHoveredIds: updateHoveredIds,
	}
}

export function useMarkHighlights({
	hoveredIds,
	prevHoveredIds,
}: {
	hoveredIds: string[]
	prevHoveredIds: string[]
}) {
	const getAllMarksNodes = useCallback((): HTMLElement[] => {
		const marksNodes = [...document.querySelectorAll(`mark[data-id]`)]
		return marksNodes as HTMLElement[]
	}, [])

	const getMarksNodesById = useCallback((id: string): HTMLElement[] => {
		const marksNodes = [...document.querySelectorAll(`mark[data-id="${id}"]`)]
		return marksNodes as HTMLElement[]
	}, [])

	const decreaseOpacity = useCallback(
		(id: string) => {
			const marksNodes = getMarksNodesById(id)

			for (const mark of marksNodes) {
				setTimeout(() => {
					mark.style.backgroundColor = mark.style.backgroundColor.replace(
						"0.4",
						"0.2",
					)
				}, 0)
			}
		},
		[getMarksNodesById],
	)

	const decreaseAllMarksNodesOpacity = useCallback(() => {
		const marksNodes = getAllMarksNodes()

		for (const mark of marksNodes) {
			setTimeout(() => {
				mark.style.backgroundColor = mark.style.backgroundColor.replace(
					"0.4",
					"0.2",
				)
				// mark.style.backgroundColor = "transparent"
			}, 0)
		}
	}, [getAllMarksNodes])

	const increaseOpacity = useCallback(
		(id: string) => {
			const marksNodes = getMarksNodesById(id)

			for (const mark of marksNodes) {
				setTimeout(() => {
					mark.style.backgroundColor = mark.style.backgroundColor.replace(
						"0.2",
						"0.4",
					)
				}, 0)
			}
		},
		[getMarksNodesById],
	)

	useEffect(() => {
		for (const id of prevHoveredIds) {
			decreaseOpacity(id)
		}

		// During first 'mouseover' event, both prevHoveredIds and hoveredIds are empty arrays
		// So we clear any opacity added on the marks
		if (!prevHoveredIds.length) {
			decreaseAllMarksNodesOpacity()
		}

		for (const id of hoveredIds) {
			increaseOpacity(id)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hoveredIds])
}
